import React from "react";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { acme, acmeMarketingHeadlineSx, acmeMarketingBodyLeadSx, acmeMarketingEyebrowSx } from "../../theme/acmeTheme";
import { Button, DashboardPreview } from "../ui";
import GlowyWavesBackground from "./GlowyWavesBackground";

const fadeUp = {
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const gradientTextSx = {
  background: `linear-gradient(90deg, ${acme.colors.brand} 0%, #16a34a 45%, ${acme.colors.foreground} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline",
};

const trustItems = ["99.9% uptime", "GDPR compliant", "24/7 support"];

/**
 * Full-viewport home hero with glow waves + gradient headline.
 */
export default function HomeHero() {
  const scrollToFeatures = () => {
    document.getElementById("features-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        width: "100%",
        minHeight: { xs: "calc(100svh - 64px)", md: "calc(100svh - 72px)" },
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        isolation: "isolate",
        ...fadeUp,
      }}
    >
      <GlowyWavesBackground />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 5, md: 6 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "center",
            gap: { xs: 5, lg: 8 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: "center", lg: "left" },
              animation: "fadeUp 0.6s ease-out forwards",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: 2.5,
                px: 1.75,
                py: 0.75,
                borderRadius: acme.radius.full,
                border: `1px solid ${acme.colors.border}`,
                backgroundColor: "rgba(255,255,255,0.72)",
                backdropFilter: "blur(8px)",
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: acme.colors.brand }} />
              <Typography
                sx={{
                  ...acmeMarketingEyebrowSx,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  color: acme.colors.mutedForegroundSolid,
                }}
              >
                Email & SMS platform
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                ...acmeMarketingHeadlineSx,
                color: acme.colors.foreground,
                fontSize: { xs: "2.35rem", sm: "3rem", md: "3.65rem", lg: "3.85rem" },
                mb: 2,
              }}
            >
              Grow faster with{" "}
              <Box component="span" sx={gradientTextSx}>
                Email & SMS
              </Box>{" "}
              that{" "}
              <Box component="span" sx={gradientTextSx}>
                actually converts
              </Box>
            </Typography>

            <Typography
              sx={{
                ...acmeMarketingBodyLeadSx,
                maxWidth: 520,
                mx: { xs: "auto", lg: 0 },
                fontSize: { xs: "1rem", sm: "1.125rem" },
                color: acme.colors.mutedForegroundSolid,
                mb: 3,
              }}
            >
              Send campaigns, automate sequences, and track opens, clicks and ROI — the same
              dashboard you&apos;ll use after signup.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 3,
                justifyContent: { xs: "center", lg: "flex-start" },
              }}
            >
              <Button
                component={Link}
                to="/register"
                sx={{ borderRadius: acme.radius.full, px: 3.5 }}
                endIcon={<FingerprintIcon sx={{ fontSize: 20 }} />}
              >
                Start free trial
              </Button>
              <Button
                variant="outline"
                sx={{
                  borderRadius: acme.radius.full,
                  px: 3.5,
                  backgroundColor: "rgba(255,255,255,0.65)",
                  backdropFilter: "blur(8px)",
                }}
                onClick={scrollToFeatures}
              >
                View features
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                justifyContent: { xs: "center", lg: "flex-start" },
              }}
            >
              {trustItems.map((item) => (
                <Box
                  key={item}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: acme.radius.full,
                    backgroundColor: "rgba(255,255,255,0.72)",
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${acme.colors.border}`,
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: acme.colors.brand }} />
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      color: acme.colors.mutedForegroundSolid,
                      fontWeight: 500,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              width: "100%",
              maxWidth: 560,
              animation: "fadeUp 0.8s ease-out 0.15s both",
            }}
          >
            <Box
              sx={{
                border: `1px solid ${acme.colors.border}`,
                p: 1,
                borderRadius: acme.radius.xl,
                backgroundColor: "rgba(255,255,255,0.82)",
                backdropFilter: "blur(12px)",
              }}
            >
              <DashboardPreview />
              <Box
                sx={{
                  position: "relative",
                  mt: -4,
                  height: 48,
                  background: `linear-gradient(to top, ${acme.colors.pageBg}, transparent)`,
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
